import type { RouterOutput } from "@/lib/trpc";

export type SearchExecutionActivity = NonNullable<
      RouterOutput["searchSpecifications"]["getSearchSpecificationActivity"]
>;

export type BrandSearchActivity = NonNullable<RouterOutput["brands"]["getBrandSearchActivity"]>;

export type SearchActivityPhase = SearchExecutionActivity["phase"];
export type SearchActivityProgressStep = NonNullable<SearchExecutionActivity["progressStep"]>;

export type SearchActivityRealtimePayload = {
      activeExecutionCount?: number;
      brandId: string;
      failureReason: SearchExecutionActivity["failureReason"];
      finalizedAt: Date | string | null;
      isActive: boolean;
      phase: SearchActivityPhase;
      phaseLabel: string;
      progressCurrent: number | null;
      progressPercentage: number | null;
      progressStep: SearchExecutionActivity["progressStep"];
      progressTotal: number | null;
      searchExecutionId: string;
      searchSpecificationId: string;
      startedAt: Date | string;
      status: SearchExecutionActivity["status"];
      updatedAt: Date | string | null;
};

const TERMINAL_PHASES = new Set<SearchActivityPhase>(["completed", "failed", "interrupted"]);

function normalizeActivityDate(value: Date | string | null | undefined): string | null {
      if (!value) {
            return null;
      }

      return new Date(value).toISOString();
}

function getActivityTimestampMs(value: Date | string | null | undefined): number | null {
      if (!value) {
            return null;
      }

      return new Date(value).getTime();
}

export function searchActivityFromRealtimePayload(payload: SearchActivityRealtimePayload): SearchExecutionActivity {
      return {
            id: payload.searchExecutionId,
            brandId: payload.brandId,
            failureReason: payload.failureReason,
            finalizedAt: normalizeActivityDate(payload.finalizedAt),
            isActive: payload.isActive,
            phase: payload.phase,
            phaseLabel: payload.phaseLabel,
            progressCurrent: payload.progressCurrent,
            progressPercentage: payload.progressPercentage,
            progressStep: payload.progressStep,
            progressTotal: payload.progressTotal,
            searchSpecificationId: payload.searchSpecificationId,
            startedAt: normalizeActivityDate(payload.startedAt) ?? new Date(payload.startedAt).toISOString(),
            status: payload.status,
            updatedAt: normalizeActivityDate(payload.updatedAt),
      };
}

export function brandActivityFromRealtimePayload(payload: SearchActivityRealtimePayload): BrandSearchActivity | null {
      const activity = searchActivityFromRealtimePayload(payload);

      if ((payload.activeExecutionCount ?? 0) <= 0 && !payload.isActive) {
            return null;
      }

      return {
            ...activity,
            activeExecutionCount: payload.activeExecutionCount ?? 1,
      };
}

export function isSearchActivityTerminal(
      activity: SearchExecutionActivity | BrandSearchActivity | null | undefined,
): boolean {
      return activity ? TERMINAL_PHASES.has(activity.phase) : false;
}

export function shouldInvalidateSearchChartsForActivity(activity: SearchExecutionActivity | null | undefined): boolean {
      return Boolean(activity && (activity.phase === "scanning" || isSearchActivityTerminal(activity)));
}

export function shouldInvalidateBrandChartsForActivity(activity: BrandSearchActivity | null | undefined): boolean {
      return Boolean(activity && (activity.phase === "scanning" || isSearchActivityTerminal(activity)));
}

export function shouldShowSearchActivityOverlay(
      activity: SearchExecutionActivity | BrandSearchActivity | null | undefined,
      options: {
            includeScanning?: boolean;
      } = {},
): boolean {
      if (!activity?.isActive) {
            return false;
      }

      if (options.includeScanning) {
            return true;
      }

      return activity.phase !== "scanning";
}

export function getSearchActivityElapsedMs(
      activity: SearchExecutionActivity | BrandSearchActivity | null | undefined,
      now = Date.now(),
): number | null {
      if (!activity) {
            return null;
      }

      const startedAt = getActivityTimestampMs(activity.startedAt);
      const finalizedAt = getActivityTimestampMs(activity.finalizedAt);

      if (startedAt === null) {
            return null;
      }

      return Math.max(0, (finalizedAt ?? now) - startedAt);
}

export function formatSearchActivityElapsed(ms: number | null): string {
      if (ms == null) {
            return "Just started";
      }

      const totalSeconds = Math.max(0, Math.floor(ms / 1000));
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      if (minutes >= 60) {
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            return `${hours}h ${remainingMinutes}m`;
      }

      if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
      }

      return `${seconds}s`;
}

export function getSearchActivityFreshnessMs(
      activity: SearchExecutionActivity | BrandSearchActivity | null | undefined,
      now = Date.now(),
): number | null {
      if (!activity) {
            return null;
      }

      const updatedAt = getActivityTimestampMs(activity.updatedAt);

      if (updatedAt !== null) {
            return Math.max(0, now - updatedAt);
      }

      return getSearchActivityElapsedMs(activity, now);
}

export function formatSearchActivityFreshness(ms: number | null): string {
      if (ms == null || ms < 5_000) {
            return "just now";
      }

      const totalSeconds = Math.floor(ms / 1000);

      if (totalSeconds < 60) {
            return `${totalSeconds}s ago`;
      }

      const totalMinutes = Math.floor(totalSeconds / 60);

      if (totalMinutes < 60) {
            return `${totalMinutes}m ago`;
      }

      const totalHours = Math.floor(totalMinutes / 60);

      if (totalHours < 24) {
            return `${totalHours}h ago`;
      }

      return `${Math.floor(totalHours / 24)}d ago`;
}

export function getDeterminateSearchActivityProgressPercentage(
      activity: SearchExecutionActivity | BrandSearchActivity | null | undefined,
): number | null {
      if (!activity || activity.progressPercentage === null) {
            return null;
      }

      if (activity.progressTotal === null || activity.progressTotal <= 0) {
            return null;
      }

      switch (activity.phase) {
            case "generating":
                  return activity.progressPercentage;
            case "scanning":
                  switch (activity.progressStep) {
                        case "analyzing":
                        case "creating":
                              return activity.progressPercentage;
                        default:
                              return null;
                  }
            default:
                  return null;
      }
}

export function getSearchActivityProgressLabel(
      activity: SearchExecutionActivity | BrandSearchActivity | null | undefined,
): string | null {
      if (!activity) {
            return null;
      }

      const { phase, progressCurrent, progressTotal, progressStep } = activity;

      if (
            progressCurrent === null ||
            progressTotal === null ||
            progressTotal <= 0 ||
            phase === "preparing" ||
            phase === "queued"
      ) {
            return null;
      }

      switch (phase) {
            case "generating":
                  return `${progressCurrent} of ${progressTotal} model runs completed`;
            case "scanning":
                  switch (progressStep) {
                        case "analyzing":
                              return `${progressCurrent} of ${progressTotal} responses analyzed`;
                        case "creating":
                              return `${progressCurrent} of ${progressTotal} competitor profiles created`;
                        default:
                              return null;
                  }
            default:
                  return null;
      }
}

export function getSearchActivitySupportingText(
      activity: SearchExecutionActivity | BrandSearchActivity | null | undefined,
): string | null {
      if (!activity) {
            return null;
      }

      const progressLabel = getSearchActivityProgressLabel(activity);

      if (progressLabel) {
            return progressLabel;
      }

      switch (activity.phase) {
            case "preparing":
                  return "Credits are reserved and this search is being prepared.";
            case "queued":
                  return "The search is queued and waiting for an available worker.";
            case "generating":
                  return activity.isActive
                        ? "Responses are being generated across the selected model runs."
                        : "Response generation finished for this run.";
            case "scanning":
                  if (!activity.isActive) {
                        return "Competitor scanning finished for this run.";
                  }

                  if (activity.progressStep === "matching") {
                        return "Competitor matching is underway.";
                  }

                  return "Competitor scanning is underway.";
            case "completed":
                  return "This run completed successfully.";
            case "failed":
                  return "This run ended before it could complete.";
            case "interrupted":
                  return "This run was interrupted before completion.";
      }
}

export function getSearchActivitySidebarLabel(
      activity: SearchExecutionActivity | BrandSearchActivity | null | undefined,
): string | null {
      if (!activity) {
            return null;
      }

      switch (activity.phase) {
            case "preparing":
                  return "Preparing";
            case "queued":
                  return "Queued";
            case "generating":
                  return "Generating";
            case "scanning":
                  return "Scanning";
            default:
                  return null;
      }
}

export function selectNewestSearchExecutionActivity(
      persistedActivity: SearchExecutionActivity | null | undefined,
      latestEvent: SearchExecutionActivity | null | undefined,
      brandId?: string | null,
): SearchExecutionActivity | null {
      if (!latestEvent) {
            return persistedActivity ?? null;
      }

      if (brandId && latestEvent.brandId !== brandId) {
            return persistedActivity ?? null;
      }

      if (!persistedActivity) {
            return latestEvent;
      }

      if (latestEvent.id === persistedActivity.id) {
            return latestEvent;
      }

      if (latestEvent.isActive !== persistedActivity.isActive) {
            return latestEvent.isActive ? latestEvent : persistedActivity;
      }

      const persistedStartedAt = getActivityTimestampMs(persistedActivity.startedAt);
      const latestEventStartedAt = getActivityTimestampMs(latestEvent.startedAt);

      if (persistedStartedAt === null || latestEventStartedAt === null) {
            return latestEvent;
      }

      return latestEventStartedAt > persistedStartedAt ? latestEvent : persistedActivity;
}
